// Surplus de matériel (décision du dirigeant du 22/10/2026, spécification métier frigoriste partie B).
// Calcul PUR, partagé serveur / interface. Décimal exact, jamais de flottant.
//
// Ordre de calcul, sans double compte (B.4) :
//   1. besoin net (quantité posée : métré, comptage, logement type, saisie) ;
//   2. UNE seule majoration : rien si la ligne vient d'un métré qui porte déjà réserve et chutes, rien si le surplus est déjà compris
//      dans le devis, sinon besoin net × (1 + taux de la famille) ;
//   3. arrondi au conditionnement ENSUITE (l'arrondi n'est pas du surplus). Conditionnement inconnu : arrondi à l'unité supérieure,
//      avec la mention « conditionnement à renseigner ».
// La famille « surplus oui / non » et le taux sont des attributs de l'article du catalogue. Un article sans famille : « surplus à
// décider », aucun taux deviné. Jamais de surplus sur les machines, les boîtiers, les réfnets, les commandes, les pompes, la
// main-d'œuvre ni le fluide (le fluide s'arrondit seulement à la bouteille entière).
import { Decimal } from 'decimal.js';

const D = Decimal.clone({ precision: 40 });

export const FAMILLES_SURPLUS = {
  tube_cuivre: { libelle: 'Tube cuivre frigorifique', surplus: true, taux: '15', raison: 'Coupes, cintrages, cheminement réel.' },
  isolant: { libelle: 'Isolant, adhésif, colle', surplus: true, taux: '15', raison: 'Jonctions, recouvrements, pertes de découpe.' },
  cable: { libelle: 'Câble de bus, de commande, d’interconnexion', surplus: true, taux: '15', raison: 'Remontées en tableau, boucles de service.' },
  goulotte: { libelle: 'Goulottes, cache-liaisons, gaines, fourreaux', surplus: true, taux: '15', raison: 'Coupes d’angle, chutes en bout de barre.' },
  supportage: { libelle: 'Supports, colliers, rails, tiges filetées, chevilles', surplus: true, taux: '20', raison: 'Petites pièces nombreuses, perdues ou abîmées.' },
  raccord: { libelle: 'Raccords cuivre, brasure, décapant', surplus: true, taux: '20', raison: 'Consommation difficile à métrer, casse, reprises.' },
  condensats: { libelle: 'Évacuation des condensats', surplus: true, taux: '15', raison: 'Pentes et cheminement réel.' },
  consommable: { libelle: 'Consommables (azote, joints, rubans, mastic)', surplus: true, taux: '20', raison: 'Consommation au chantier.' },
  fluide: { libelle: 'Fluide frigorigène d’appoint', surplus: false, taux: null, raison: 'Charge pesée et inscrite au registre : arrondi à la bouteille entière seulement.' },
  unite: { libelle: 'Unités extérieures et intérieures', surplus: false, taux: null, raison: 'Chaque appareil est compté et repéré.' },
  derivation: { libelle: 'Boîtiers de dérivation, joints de dérivation (réfnets)', surplus: false, taux: null, raison: 'Pièces fabricant comptées une à une.' },
  commande: { libelle: 'Télécommandes, commandes centralisées, passerelles, sondes', surplus: false, taux: null, raison: 'Équipements comptés.' },
  equipement_unitaire: { libelle: 'Pompes de relevage, plots, châssis, supports de groupe', surplus: false, taux: null, raison: 'Un par appareil : quantités exactes.' },
  service: { libelle: 'Main-d’œuvre, location, sous-traitance', surplus: false, taux: null, raison: 'Le surplus porte sur la matière, jamais sur les heures.' },
} as const satisfies Record<string, { libelle: string; surplus: boolean; taux: string | null; raison: string }>;

export type FamilleSurplus = keyof typeof FAMILLES_SURPLUS;
export const CODES_FAMILLES = Object.keys(FAMILLES_SURPLUS) as FamilleSurplus[];
export const estFamille = (v: unknown): v is FamilleSurplus => typeof v === 'string' && v in FAMILLES_SURPLUS;

/** Bornes du taux saisissable et plage recommandée (décision du 22/10 : 15 à 20 %). */
export const TAUX_MIN = '0';
export const TAUX_MAX = '30';
export const TAUX_RECOMMANDE = { min: '15', max: '20' } as const;

export type StatutSurplus = 'surplus' | 'sans_surplus' | 'a_decider' | 'deja_majoree' | 'deja_au_devis';
export const LIBELLES_STATUT_SURPLUS: Record<StatutSurplus, string> = {
  surplus: 'Surplus proposé',
  sans_surplus: 'Pas de surplus',
  a_decider: 'Surplus à décider : famille de l’article à préciser',
  deja_majoree: 'Déjà majorée au métré (réserve et chutes)',
  deja_au_devis: 'Surplus déjà compris dans le devis',
};

/** Réserve et chutes d'un métré de tracé (null = la ligne ne vient pas d'un métré majoré). */
export interface MajorationMetre { reservePct: string | null; chutesPct: string | null; variable: boolean }

export interface EntreeMajoration {
  besoin: string;
  famille: FamilleSurplus | null;
  /** Taux réglé sur l'article du catalogue (null = taux par défaut de la famille). */
  tauxArticle?: string | null;
  /** Taux choisi pour CE devis ou CE bon (null = taux de l'article). */
  tauxSaisi?: string | null;
  metre?: MajorationMetre | null;
  /** Surplus déjà compris dans les quantités du devis accepté (commande seulement). */
  dejaAuDevis?: boolean;
  /** false : le surplus proposé est retiré (case décochée côté commande). */
  appliquer?: boolean;
}

export interface Majoration {
  statut: StatutSurplus;
  famille: FamilleSurplus | null;
  taux: string | null;
  source_taux: 'saisi' | 'article' | 'famille' | null;
  /** Quantité de surplus (0 si aucun, ou retiré). */
  quantite: string;
  besoin_majore: string;
  applique: boolean;
  detail: string;
}

const pct = (x: string | null) => (x === null ? '?' : new D(x).toFixed().replace('.', ','));

export function texteMetre(m: MajorationMetre) {
  return m.variable ? 'réserve et chutes selon les tracés' : `réserve ${pct(m.reservePct)} % + chutes ${pct(m.chutesPct)} %`;
}

/** Contrôle d'un taux saisi : 0 à 30 %, deux décimales ; hors 15–20 %, une justification écrite est exigée. Renvoie un message ou null. */
export function controleTaux(taux: string, motif: string | null | undefined): string | null {
  const t = taux.trim().replace(',', '.');
  if (!/^\d{1,2}(\.\d{1,2})?$/.test(t)) return 'Taux de surplus : nombre de 0 à 30, deux décimales au plus.';
  const v = new D(t);
  if (v.lt(TAUX_MIN) || v.gt(TAUX_MAX)) return 'Taux de surplus : entre 0 et 30 %.';
  if ((v.lt(TAUX_RECOMMANDE.min) || v.gt(TAUX_RECOMMANDE.max)) && (motif ?? '').trim().length < 5) {
    return 'Taux hors de la plage recommandée (15 à 20 %) : écrivez la raison (5 caractères au moins).';
  }
  return null;
}
export const horsRecommandation = (taux: string | null) => taux !== null && (new D(taux).lt(TAUX_RECOMMANDE.min) || new D(taux).gt(TAUX_RECOMMANDE.max));

/** Étapes 1 et 2 : une seule majoration par ligne. */
export function majorer(e: EntreeMajoration): Majoration {
  const besoin = new D(e.besoin);
  const base = { famille: e.famille, taux: null, source_taux: null, quantite: '0', besoin_majore: besoin.toFixed(), applique: false } as const;
  if (e.dejaAuDevis) return { ...base, statut: 'deja_au_devis', detail: 'Surplus déjà compris dans les quantités du devis accepté : rien n’est ajouté.' };
  if (e.metre) return { ...base, statut: 'deja_majoree', detail: `Déjà majorée au métré : ${texteMetre(e.metre)}. Pas de surplus en plus.` };
  if (!e.famille) return { ...base, statut: 'a_decider', detail: 'Famille de l’article non renseignée : surplus à décider dans la bibliothèque de prix. Aucun taux n’est deviné.' };
  const f = FAMILLES_SURPLUS[e.famille];
  if (!f.surplus) return { ...base, statut: 'sans_surplus', detail: f.raison };
  const [taux, source] = e.tauxSaisi != null ? [e.tauxSaisi, 'saisi' as const] : e.tauxArticle != null ? [e.tauxArticle, 'article' as const] : [f.taux!, 'famille' as const];
  const t = new D(taux);
  const appliquer = e.appliquer !== false && t.gt(0);
  const q = appliquer ? besoin.mul(t).div(100) : new D(0);
  return { statut: t.gt(0) ? 'surplus' : 'sans_surplus', famille: e.famille, taux: t.toFixed(), source_taux: source, quantite: q.toFixed(),
    besoin_majore: besoin.plus(q).toFixed(), applique: appliquer,
    detail: t.gt(0) ? `${f.libelle} : ${t.toFixed().replace('.', ',')} %${e.appliquer === false ? ' (retiré)' : ''}` : `${f.libelle} : taux de 0 % choisi.` };
}

export interface Arrondi { nombre: string | null; quantite: string; arrondi: string; inconnu: boolean }

/** Étape 3 : arrondi au conditionnement (quantité exprimée dans l'unité de l'article). Inconnu : unité supérieure. */
export function arrondir(besoinMajore: string, conditionnement: string | null): Arrondi {
  const b = new D(besoinMajore);
  if (!conditionnement) {
    const q = b.ceil();
    return { nombre: null, quantite: q.toFixed(), arrondi: q.minus(b).toFixed(), inconnu: true };
  }
  const n = b.div(conditionnement).ceil();
  const q = n.mul(conditionnement);
  return { nombre: n.toFixed(), quantite: q.toFixed(), arrondi: q.minus(b).toFixed(), inconnu: false };
}

export interface PartGlobale { nombre: string | null; quantite: string; avance: string }
export interface RepartitionGlobale { nombre: string | null; quantite: string; arrondi: string; parts: PartGlobale[]; reste: string }

/**
 * Commande globale répartie : une seule quantité arrondie sur le total, puis répartition en conditionnements ENTIERS dans l'ordre
 * des dates (les besoins sont donnés dans cet ordre). Chaque lot reçoit ce qu'il faut pour couvrir les besoins cumulés à sa date ;
 * `avance` = ce qui est livré en avance pour les lots suivants ; `reste` = ce qui dépasse le dernier besoin (arrondi final), signalé.
 */
export function repartirGlobal(besoinsMajores: string[], conditionnement: string | null): RepartitionGlobale {
  const pas = new D(conditionnement ?? 1);
  let cumul = new D(0); let alloue = new D(0);
  const parts: PartGlobale[] = [];
  for (const b of besoinsMajores) {
    cumul = cumul.plus(b);
    const k = D.max(0, cumul.div(pas).ceil().minus(alloue));
    alloue = alloue.plus(k);
    parts.push({ nombre: conditionnement ? k.toFixed() : null, quantite: k.mul(pas).toFixed(), avance: alloue.mul(pas).minus(cumul).toFixed() });
  }
  const total = alloue.mul(pas);
  return { nombre: conditionnement ? alloue.toFixed() : null, quantite: total.toFixed(), arrondi: total.minus(cumul).toFixed(), parts,
    reste: total.minus(cumul).toFixed() };
}

/** Réserve / chutes portées par l'instantané d'un métré de tracé (câbles, tuyauteries). null = pas un métré majoré. */
export function majorationDuMetre(snapshot: unknown, dedupKey: string | null): MajorationMetre | null {
  const elements = Array.isArray(snapshot) ? snapshot : [];
  const avec = elements.filter((s: any) => s && typeof s === 'object' && ('reservePct' in s || 'chutesPct' in s));
  if (!avec.length) return dedupKey?.startsWith('conception:cablage:') ? { reservePct: null, chutesPct: null, variable: true } : null;
  const v = (x: unknown) => (x === null || x === undefined || x === '' ? null : new D(String(x)).toFixed());
  const cles = new Set(avec.map((s: any) => `${v(s.reservePct)}|${v(s.chutesPct)}`));
  const premier = avec[0] as any;
  return { reservePct: v(premier.reservePct), chutesPct: v(premier.chutesPct), variable: cles.size > 1 };
}
